import { Link } from "react-router-dom";
import { useState } from "react";
import { Instagram, Youtube, Mail, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";
import { BRAND, NAV_LINKS } from "../data/staticData";
import api from "../lib/api";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("You're on the list. Stay frosty.");
      setEmail("");
    } catch {
      toast.error("Couldn't subscribe. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[#0A0A0A] border-t border-white/10 mt-24"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#7CFF00] to-transparent" />

      <div className="container-fld py-16 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-4 space-y-5">
          <img
            src={BRAND.logos.primary}
            alt="Frontline Defense"
            data-testid="footer-logo"
            className="h-40 w-auto drop-shadow-[0_0_36px_rgba(124,255,0,0.4)]"
          />
          <p className="text-zinc-400 leading-relaxed text-sm max-w-sm">
            Veteran-owned, USCCA Certified personal protection training. Real-world
            instruction. No theatrics. Just preparation.
          </p>
          <div className="flex gap-3">
            <a
              data-testid="footer-instagram"
              href={BRAND.instagram}
              target="_blank"
              rel="noreferrer"
              className="w-10 h-10 border border-white/15 flex items-center justify-center hover:border-[#7CFF00] hover:text-[#7CFF00] transition-colors text-white"
              aria-label="Instagram"
            >
              <Instagram className="w-4 h-4" />
            </a>
            <a
              data-testid="footer-youtube"
              href={BRAND.youtube}
              target="_blank"
              rel="noreferrer"
              className="w-10 h-10 border border-white/15 flex items-center justify-center hover:border-[#7CFF00] hover:text-[#7CFF00] transition-colors text-white"
              aria-label="YouTube"
            >
              <Youtube className="w-4 h-4" />
            </a>
          </div>
        </div>

        <div className="md:col-span-2 space-y-4">
          <h4 className="overline">Navigate</h4>
          <ul className="space-y-2">
            {NAV_LINKS.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  className="font-tactical uppercase tracking-wider text-sm text-zinc-300 hover:text-[#7CFF00] transition-colors"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-4 space-y-4">
          <h4 className="overline">Contact</h4>
          <ul className="space-y-3 text-sm text-zinc-300">
            <li className="flex items-start gap-2">
              <Phone className="w-4 h-4 mt-1 text-[#7CFF00] flex-shrink-0" />
              <a
                href={`tel:${BRAND.phoneRaw}`}
                data-testid="footer-phone"
                className="hover:text-[#7CFF00]"
              >
                {BRAND.phone}
              </a>
            </li>
            <li className="flex items-start gap-2">
              <Mail className="w-4 h-4 mt-1 text-[#7CFF00] flex-shrink-0" />
              <a
                href={`mailto:${BRAND.email}`}
                className="hover:text-[#7CFF00] text-xs whitespace-nowrap"
              >
                {BRAND.email}
              </a>
            </li>
            <li className="flex items-start gap-2">
              <MapPin className="w-4 h-4 mt-1 text-[#7CFF00] flex-shrink-0" />
              <span>{BRAND.location}</span>
            </li>
          </ul>
        </div>

        <div className="md:col-span-3 space-y-4">
          <h4 className="overline">Stay In The Loop</h4>
          <p className="text-sm text-zinc-400">
            Get class openings, range nights, and merch drops first.
          </p>
          <form
            onSubmit={onSubmit}
            data-testid="footer-newsletter-form"
            className="flex flex-col gap-2"
          >
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              data-testid="footer-newsletter-input"
              className="bg-[#151515] border border-white/15 px-4 py-3 text-sm focus:border-[#7CFF00] focus:outline-none transition-colors text-white placeholder:text-zinc-500"
            />
            <button
              type="submit"
              disabled={submitting}
              data-testid="footer-newsletter-submit"
              className="btn-primary justify-center text-sm py-3 disabled:opacity-50"
            >
              {submitting ? "Sending..." : "Enlist"}
            </button>
          </form>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container-fld py-6 flex flex-col md:flex-row justify-between items-center gap-3 text-xs text-zinc-500 font-mono-tactical uppercase tracking-widest">
          <span>© {new Date().getFullYear()} Frontline Defense LLC. All rights reserved.</span>
          <span className="text-[#7CFF00]">Protection Through Preparation</span>
        </div>
      </div>
    </footer>
  );
}
